import React, { useCallback, useMemo } from 'react';
import { Box, FeeLabel, Inscription, ListItem, ListItemBase, ListItemType } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import PaymentTypeIcon from '@/components/elements/PaymentTypeIcon';
import { useOrganizationProperties } from '@/modules/transaction';
import { Status } from '@/modules/transaction/components/ActivityStatus/ActivityStatus';
import PaymentMethodDescription from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/PaymentMethodLabel/PaymentMethodDescription';
import {
  PaymentMethod,
  PaymentMethodVerificationStatus,
} from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { PaymentMethodType } from '@/modules/user/user.types';
import theme from '@/styles/theme';

import { getPaymentMethodDetails } from '../getPaymentMethodName';
import PaymentTypeIconWrapper from '../PaymentTypeIconWrapper';
import { PlaidDirectDepositBanner } from '../PlaidDirectDepositBanner';

const highlightedRowStyle = {
  borderSize: '2px',
  borderColor: theme.colors.accentActive,
  boxShadow: theme.shadows.highlighted['box-shadow'],
};

const showRowBanner = (paymentMethod: PaymentMethod) => {
  return (
    paymentMethod?.type === PaymentMethodType.DIRECT_DEPOSIT &&
    (paymentMethod.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
      paymentMethod.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
      paymentMethod.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL ||
      paymentMethod.verificationStatus === PaymentMethodVerificationStatus.EXPIRED)
  );
};

const usePaymentMethodsList = (
  paymentMethods?: PaymentMethod[],
  highlightedPaymentMethodId?: string,
  highlightedPaymentMethodIds?: string[],
  refetchMethods?: () => void
) => {
  const {
    organizationProperties: { allowedPaymentMethodConfigs },
  } = useOrganizationProperties();
  const sortedPaymentMethods: PaymentMethod[] = useMemo(() => {
    if (!paymentMethods?.length) {
      return [];
    }

    return [...paymentMethods].sort((prev, next) => toNumber(prev.id) - toNumber(next.id));
  }, [paymentMethods]);

  const isSomeMethodHasBanner = useMemo(() => {
    return sortedPaymentMethods.some((method) => showRowBanner(method));
  }, [sortedPaymentMethods]);

  const headers: ListItemBase[] = useMemo(
    () => [
      {
        key: 'paymentType',
        title: 'Payment type',
        flex: 5,
      },
      {
        key: 'additionalInfo',
        title: 'Additional info',
        flex: 6,
      },
      {
        key: 'isDefault',
        title: 'Is default?',
        flex: isSomeMethodHasBanner ? 2 : 4,
      },
      ...(isSomeMethodHasBanner
        ? [
            {
              key: 'status',
              title: '',
              flex: 3,
            },
          ]
        : []),
    ],
    [isSomeMethodHasBanner]
  );

  const getPaymentEmployeeFee = useCallback(
    (paymentMethodType?: PaymentMethodType) =>
      allowedPaymentMethodConfigs?.find((item) => item?.paymentMethodType === paymentMethodType)
        ?.paymentEmployeeFee,
    [allowedPaymentMethodConfigs]
  );

  const formattedList = useMemo<ListItem[]>(
    () =>
      sortedPaymentMethods?.map((paymentMethod) => ({
        id: paymentMethod?.id,
        rowStyle: {
          paddingInline: theme.spacings.spacing16,
          minHeight: 76,
          ...(highlightedPaymentMethodId && highlightedPaymentMethodId === paymentMethod?.id
            ? highlightedRowStyle
            : undefined),
          ...(highlightedPaymentMethodIds &&
          paymentMethod?.id &&
          highlightedPaymentMethodIds.includes(paymentMethod?.id) &&
          !showRowBanner(paymentMethod)
            ? highlightedRowStyle
            : undefined),
        },
        dropAlign: { bottom: 'top', right: 'right' },
        ...(showRowBanner(paymentMethod)
          ? {
              rowBanner: (
                <PlaidDirectDepositBanner
                  status={paymentMethod?.verificationStatus}
                  isHighlighted={Boolean(
                    paymentMethod?.id &&
                    highlightedPaymentMethodIds &&
                    highlightedPaymentMethodIds.includes(paymentMethod?.id)
                  )}
                  accountId={paymentMethod?.accountId}
                  refetch={refetchMethods}
                />
              ),
            }
          : {}),
        fields: [
          {
            key: 'paymentType',
            type: ListItemType.Node,
            title: paymentMethod?.type || '',
            node: (
              <Box margin={{ right: 'spacing32' }} direction="row" align="center">
                <PaymentTypeIconWrapper>
                  <PaymentTypeIcon paymentType={paymentMethod?.type} />
                </PaymentTypeIconWrapper>
                <Box>
                  <Box direction="row" align="center">
                    <Inscription margin={{ left: 'spacing12' }} size="16px">
                      {getPaymentMethodDetails(paymentMethod)?.typeName}
                    </Inscription>
                  </Box>
                  <Box margin={{ left: 'spacing12', top: 'spacing4' }}>
                    <FeeLabel
                      amount={getPaymentEmployeeFee(paymentMethod?.type)}
                      testId={paymentMethod?.type}
                    />
                  </Box>
                </Box>
              </Box>
            ),
            flex: 5,
          },
          {
            key: 'additionalInfo',
            type: ListItemType.Node,
            title: paymentMethod?.accountDescription || '',
            node: (
              <Box margin={{ right: 'spacing32' }}>
                <PaymentMethodDescription paymentMethod={paymentMethod} />
              </Box>
            ),
            flex: 6,
            style: { fontSize: theme.fonts.text16Medium['font-size'] },
          },
          {
            key: 'isDefault',
            type: ListItemType.Text,
            title: paymentMethod?.isDefault ? 'Yes' : '',
            flex: isSomeMethodHasBanner ? 2 : 4,
            style: { fontSize: theme.fonts.text16Medium['font-size'] },
          },
          ...(showRowBanner(paymentMethod)
            ? [
                {
                  key: 'status',
                  type: ListItemType.Node,
                  title: '',
                  node: (
                    <Box>
                      {(paymentMethod?.verificationStatus ===
                        PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
                        paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED) && (
                        <Status denied data-testid="plaid-verification-fail">
                          Verification failed
                        </Status>
                      )}
                      {(paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
                        paymentMethod?.verificationStatus ===
                          PaymentMethodVerificationStatus.PENDING_MANUAL) && (
                        <Status pending data-testid="plaid-verification-pending">
                          Pending
                        </Status>
                      )}
                    </Box>
                  ),
                  flex: 3,
                },
              ]
            : []),
          ...(!showRowBanner(paymentMethod) && isSomeMethodHasBanner
            ? [
                {
                  key: 'status',
                  type: ListItemType.Text,
                  title: '',
                  flex: 3,
                },
              ]
            : []),
        ],
      })) as ListItem[],
    [
      getPaymentEmployeeFee,
      sortedPaymentMethods,
      highlightedPaymentMethodId,
      highlightedPaymentMethodIds,
      isSomeMethodHasBanner,
      refetchMethods,
    ]
  );

  const rawList = useMemo(
    () =>
      sortedPaymentMethods.map((paymentMethod) => ({
        paymentMethod,
        employeeFee: getPaymentEmployeeFee(paymentMethod?.type),
        isBannerShown: showRowBanner(paymentMethod),
        isHighlighted: Boolean(
          (highlightedPaymentMethodId && highlightedPaymentMethodId === paymentMethod?.id) ||
          (highlightedPaymentMethodIds &&
            paymentMethod?.id &&
            highlightedPaymentMethodIds.includes(paymentMethod?.id) &&
            !showRowBanner(paymentMethod))
        ),
      })),
    [sortedPaymentMethods, getPaymentEmployeeFee, highlightedPaymentMethodId, highlightedPaymentMethodIds]
  );

  return {
    headers,
    formattedList,
    rawList,
  };
};

export default usePaymentMethodsList;
